<template>
  <div class="app-container transformer-overview">
    <!-- 一、搜索区域：省 / 市 / 区县 / 街道 / 社区 / 站点 级联查询 -->
    <el-card shadow="never" class="search-card">
      <el-form ref="filterForm" :inline="true" size="small" @keyup.enter.native="toFilter">
        <el-form-item v-for="item in areaLevels" :key="item.level" :label="item.label">
          <el-select
            v-model="filter[item.key]"
            placeholder="全部"
            :disabled="isAreaDisabled(item.level)"
            style="width: 150px"
            @change="onAreaChange(item.level)"
          >
            <el-option label="全部" value=""></el-option>
            <el-option
              v-for="option in regionOptions[item.level]"
              :key="option.id"
              :label="option.Text"
              :value="option.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button icon="el-icon-search" type="primary" @click="toFilter">{{ $t("publics.querybtn") }}</el-button>
          <el-button icon="el-icon-refresh" @click="onResetFilter">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 二、数据统计展示区域（统计当前搜索条件下的台区及设备数据，仅展示不可点击） -->
    <el-card shadow="never" class="statistic-card">
      <div slot="header" class="card-header">
        <span class="card-title">数据统计</span>
        <span class="card-sub-title">{{ statisticScopeText }}</span>
      </div>

      <div class="stat-grid" v-loading="statisticLoading">
        <div
          v-for="item in statCards"
          :key="item.valueKey"
          class="stat-item"
          :class="[item.colorClass, { 'stat-item-primary': item.primary }]"
        >
          <div class="stat-item-label">
            <i class="stat-item-dot"></i>{{ item.label }}
          </div>
          <div class="stat-item-value">
            {{ formatStatValue(statistic[item.valueKey]) }}
            <span v-if="item.unit" class="stat-item-unit">{{ item.unit }}</span>
          </div>
        </div>
      </div>

      <!-- <div class="stat-demo-tip" v-if="statisticIsDemo">
        <i class="el-icon-info"></i>
        统计数据接口暂未开放，当前展示示例数据
      </div> -->
    </el-card>

    <!-- 三、分页列表展示区域 -->
    <el-card shadow="never" class="table-card">
      <div slot="header" class="card-header">
        <span class="card-title">台区列表</span>
        <span class="card-sub-title">共 {{ pageParams.total }} 个台区</span>
      </div>

      <el-table ref="mainTable" :data="tableData" height="380" border stripe v-loading="loading">
        <el-table-column label="台区ID" prop="ID" width="110" align="center"></el-table-column>
        <el-table-column label="台区名称" prop="name" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column label="所属区域" prop="region_name" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column label="变压器容量" width="130" align="center">
          <template slot-scope="{ row }">{{ row.capacity }} kVA</template>
        </el-table-column>
        <el-table-column label="当前负载率" width="120" align="center">
          <template slot-scope="{ row }">{{ row.load_rate }}%</template>
        </el-table-column>
        <el-table-column label="状态" width="130" align="center">
          <template slot-scope="{ row }">
            <el-tag size="small" :type="statusTagType(row.status)">{{ row.status_name }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('publics.operation')" width="120" align="center">
          <template slot-scope="{ row }">
            <el-button type="text" @click="onViewTopo(row)">查看拓扑</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="my-pagination">
        <el-pagination
          background
          layout="sizes,total,prev,pager,next,jumper"
          :total="pageParams.total"
          :page-size="pageParams.limit"
          :page-sizes="[5, 10, 50, 100, 500, 1000]"
          :current-page="pageParams.page"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 四、拓扑图数据展示区域 -->
    <el-card shadow="never" class="topo-card" v-if="topoVisible">
      <div slot="header" class="card-header">
        <span class="card-title">{{ currentTransformer.name }} - 拓扑关系图</span>
        <el-button type="text" @click="onCloseTopo">收起</el-button>
      </div>

      <div class="topo-layout">
        <!-- 左侧：拓扑关系图（占比 60%，高度不限，宽度超出可横向滚动查看） -->
        <div class="topo-chart-wrap">
          <div ref="topoScroll" class="topo-chart-scroll" v-loading="topoLoading">
            <div
              ref="topoChart"
              class="topo-chart"
              :style="{ width: topoChartWidth + 'px', height: topoChartHeight + 'px' }"
            ></div>
          </div>
          <div class="topo-legend">
            <span v-for="legend in topoLegends" :key="legend.type" class="topo-legend-item">
              <i class="topo-legend-dot" :style="{ backgroundColor: legend.color }"></i>{{ legend.label }}
            </span>
            <span class="topo-legend-tip">点击电表箱/控制箱节点可展开聚焦，点击分支箱可查看 15/18 路费控表状态</span>
          </div>
        </div>

        <!-- 右侧：节点详细信息（点击节点切换，占比 40%，高度不限，宽度不足可横向滚动） -->
        <div class="topo-info-wrap">
          <!-- 费控表监测数据（key 保证与节点信息视图互相销毁重建，避免残留图表 DOM） -->
          <div v-if="infoView === 'meter'" key="meterView" class="meter-view">
            <div class="topo-info-header">
              <el-button type="text" icon="el-icon-back" @click="onBackToNodeInfo">返回</el-button>
              <span class="topo-info-title">通道 {{ currentMeter.code }}的费控表 {{ currentMeter.address }} 监测数据</span>
            </div>
            <div class="meter-body" v-loading="meterLoading">
              <div v-if="meterMonitorData" ref="meterChart" class="meter-chart"></div>
              <div v-else class="meter-empty">暂无监测数据</div>
            </div>
          </div>

          <!-- 节点信息 -->
          <div v-else key="nodeView" class="node-view" v-loading="nodeDetailLoading">
            <div class="topo-info-header">
              <span class="topo-info-title">{{ nodeInfoTitle }}</span>
              <el-tag v-if="nodeInfo.node_type_name" size="mini" type="info">{{ nodeInfo.node_type_name }}</el-tag>
            </div>

            <!-- 第一层：台区 -->
            <el-descriptions v-if="nodeInfo.node_type === 'transformer'" :column="1" border size="small">
              <el-descriptions-item label="台区ID">{{ nodeInfo.ID || "-" }}</el-descriptions-item>
              <el-descriptions-item label="台区名称">{{ nodeInfo.name || "-" }}</el-descriptions-item>
              <el-descriptions-item label="变压器容量">
                {{ nodeInfo.capacity != null ? nodeInfo.capacity + " kVA" : "-" }}
              </el-descriptions-item>
              <el-descriptions-item label="当前负载率">
                {{ nodeInfo.load_rate != null ? nodeInfo.load_rate + "%" : "-" }}
              </el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag v-if="nodeInfo.status_name" size="mini" :type="statusTagType(nodeInfo.status)">
                  {{ nodeInfo.status_name }}
                </el-tag>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="资产码">{{ nodeInfo.asset_code || "-" }}</el-descriptions-item>
              <el-descriptions-item label="所属区域">{{ nodeInfo.region_name || "-" }}</el-descriptions-item>
              <el-descriptions-item label="地址">{{ nodeInfo.address || "-" }}</el-descriptions-item>
              <el-descriptions-item label="是否投运">{{ formatOperation(nodeInfo.is_operation) }}</el-descriptions-item>
              <el-descriptions-item label="累积电能量">{{ formatEnergy(nodeInfo.total_elect) }}</el-descriptions-item>
              <el-descriptions-item label="创建时间">{{ nodeInfo.create_time || "-" }}</el-descriptions-item>
            </el-descriptions>

            <!-- 第二层：国网电表箱（id 为空表示未录入电表号） -->
            <el-descriptions v-else-if="nodeInfo.node_type === 'meter_box'" :column="1" border size="small">
              <el-descriptions-item label="电表箱名称">{{ nodeInfo.name || "-" }}</el-descriptions-item>
              <el-descriptions-item label="电表号">
                <span v-if="nodeInfo.ID">{{ nodeInfo.ID }}</span>
                <el-tag v-else size="mini" type="warning">未录入电表号</el-tag>
              </el-descriptions-item>
              <el-descriptions-item label="账户信息">{{ nodeInfo.account_info || "-" }}</el-descriptions-item>
              <el-descriptions-item label="账户余额">
                {{ nodeInfo.account_balance != null ? formatAmount(nodeInfo.account_balance) + " 元" : "-" }}
              </el-descriptions-item>
              <el-descriptions-item label="最大容量">
                {{ nodeInfo.max_capacity != null ? nodeInfo.max_capacity + " kVA" : "-" }}
              </el-descriptions-item>
            </el-descriptions>

            <!-- 第三层：一体柜 / 有序充电控制箱 -->
            <el-descriptions
              v-else-if="isLevel3Type(nodeInfo.node_type)"
              :column="1"
              border
              size="small"
            >
              <el-descriptions-item :label="nodeInfo.node_type_name || '一体柜'">
                {{ nodeInfo.name || "-" }}
              </el-descriptions-item>
              <el-descriptions-item label="设备ID">{{ nodeInfo.ID || "-" }}</el-descriptions-item>
              <el-descriptions-item label="资产码">{{ nodeInfo.asset_code || "-" }}</el-descriptions-item>
              <el-descriptions-item label="负荷控制开关状态">
                <el-tag
                  v-if="nodeInfo.switch_status"
                  size="mini"
                  :type="nodeInfo.switch_status === '闭合' ? 'success' : 'info'"
                >
                  {{ nodeInfo.switch_status }}
                </el-tag>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="当前充电状态">{{ nodeInfo.charge_status || "-" }}</el-descriptions-item>
              <el-descriptions-item label="当前铜排温度">
                {{ nodeInfo.copper_temp != null ? nodeInfo.copper_temp + " ℃" : "-" }}
              </el-descriptions-item>
              <el-descriptions-item label="终端信息">
                <div v-if="nodeInfo.terminal" class="terminal-info">
                  <div class="terminal-line">{{ nodeInfo.terminal.model }}</div>
                  <div class="terminal-line">序列号：{{ nodeInfo.terminal.serial_no }}</div>
                  <div class="terminal-line">SIM卡号：{{ nodeInfo.terminal.sim_no }}</div>
                  <div class="terminal-line">SIM到期：{{ nodeInfo.terminal.sim_expire }}</div>
                  <div class="terminal-line">
                    <el-tag size="mini" :type="nodeInfo.terminal.online ? 'success' : 'danger'">
                      {{ nodeInfo.terminal.online_name }}
                    </el-tag>
                  </div>
                </div>
                <span v-else>-</span>
              </el-descriptions-item>
              <el-descriptions-item label="设备地址">{{ nodeInfo.address || "-" }}</el-descriptions-item>
              <el-descriptions-item label="下级分支箱数量">
                {{ nodeInfo.branch_box_nums != null ? nodeInfo.branch_box_nums + " 个" : "-" }}
              </el-descriptions-item>
              <el-descriptions-item label="电表号">{{ nodeInfo.elect_tb_no || "-" }}</el-descriptions-item>
              <el-descriptions-item label="CT号">{{ nodeInfo.ct_no || "-" }}</el-descriptions-item>
              <el-descriptions-item label="是否投运">{{ formatOperation(nodeInfo.is_operation) }}</el-descriptions-item>
              <el-descriptions-item label="当前设备累积电能量">{{ formatEnergy(nodeInfo.total_elect) }}</el-descriptions-item>
              <el-descriptions-item label="所有下级分支箱累计电能量">{{ formatEnergy(nodeInfo.sub_total_elect) }}</el-descriptions-item>
            </el-descriptions>

            <!-- 第四层：分支箱（含 15 路费控表状态） -->
            <template v-else-if="nodeInfo.node_type === 'branch_box'">
              <el-descriptions :column="1" border size="small">
                <el-descriptions-item label="分支箱ID">{{ nodeInfo.ID || "-" }}</el-descriptions-item>
                <el-descriptions-item label="分支箱名称">{{ nodeInfo.name || "-" }}</el-descriptions-item>
                <el-descriptions-item label="资产码">{{ nodeInfo.asset_code || "-" }}</el-descriptions-item>
                <el-descriptions-item label="当前状态">
                  <el-tag v-if="nodeInfo.status_name" size="mini" :type="statusTagType(nodeInfo.status)">
                    {{ nodeInfo.status_name }}
                  </el-tag>
                  <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="箱内温度">
                  {{ nodeInfo.box_temp != null ? nodeInfo.box_temp + " ℃" : "-" }}
                </el-descriptions-item>
                <el-descriptions-item label="门禁状态">
                  <el-tag v-if="nodeInfo.door_status" size="mini" :type="nodeInfo.door_status === '关闭' ? 'success' : 'warning'">
                    {{ nodeInfo.door_status }}
                  </el-tag>
                  <span v-else>-</span>
                </el-descriptions-item>
                <el-descriptions-item label="SIM卡号">{{ nodeInfo.sim_no || "-" }}</el-descriptions-item>
                <el-descriptions-item label="SIM到期">{{ nodeInfo.sim_expire || "-" }}</el-descriptions-item>
                <el-descriptions-item label="箱体地址">{{ nodeInfo.address || "-" }}</el-descriptions-item>
                <el-descriptions-item label="所属一体柜">{{ nodeInfo.power_cabinet_id || "-" }}</el-descriptions-item>
                <el-descriptions-item label="是否投运">{{ formatOperation(nodeInfo.is_operation) }}</el-descriptions-item>
                <el-descriptions-item label="当前设备累积电能量">{{ formatEnergy(nodeInfo.total_elect) }}</el-descriptions-item>
              </el-descriptions>

              <div class="fee-meter-panel" v-loading="feeTableLoading">
                <div class="fee-meter-title">
                  费控表状态
                  <span class="fee-meter-sub-title" v-if="feeTableList.length">共 {{ feeTableList.length }} 路</span>
                  <el-tooltip placement="right" effect="dark">
                    <div slot="content" class="fee-meter-tips">
                      <div class="fee-meter-tips-row">
                        <span class="fee-meter-tips-label">方形（安装状态）：</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-shape shape-install shape-on"></i>已安装</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-shape shape-install shape-off"></i>未安装</span>
                      </div>
                      <div class="fee-meter-tips-row">
                        <span class="fee-meter-tips-label">圆形（通信状态）：</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-shape shape-comm shape-on"></i>在线</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-shape shape-comm shape-off"></i>离线</span>
                      </div>
                      <div class="fee-meter-tips-row">
                        <span class="fee-meter-tips-label">三角形（绑定状态）：</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-shape shape-bind shape-on"></i>已绑用户</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-shape shape-bind shape-off"></i>未绑用户</span>
                      </div>
                      <div class="fee-meter-tips-row">
                        <span class="fee-meter-tips-label">通道标识：</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-channel channel-public">公</i>公桩通道</span>
                        <span class="fee-meter-tips-item"><i class="fee-meter-tips-channel channel-private">私</i>私桩通道</span>
                      </div>
                    </div>
                    <i class="el-icon-question fee-meter-help"></i>
                  </el-tooltip>
                </div>
                <template v-if="feeTableList.length">
                  <div v-for="phase in feeMeterPhases" :key="phase.key" class="fee-meter-phase">
                    <div class="fee-meter-phase-label" :class="'fee-meter-phase-' + phase.key">
                      {{ phase.label }}（{{ getPhaseMeters(phase.key).length }}路）
                    </div>
                    <div class="fee-meter-list">
                      <div
                        v-for="meter in getPhaseMeters(phase.key)"
                        :key="meter.ID || meter.code"
                        class="fee-meter-item"
                        :class="['fee-meter-item-' + meter.run_status, { 'fee-meter-item-disabled': !meter.installed }]"
                        @click="onViewMeterMonitor(meter)"
                      >
                        <div class="fee-meter-head">
                          <span class="fee-meter-code">{{ meter.code }}</span>
                          <!-- 公桩通道 / 私桩通道 -->
                          <span
                            class="fee-meter-channel"
                            :class="meter.is_public_channel ? 'fee-meter-channel-public' : 'fee-meter-channel-private'"
                            :title="meter.channel_type_name"
                          >
                            {{ meter.is_public_channel ? "公" : "私" }}
                          </span>
                        </div>
                        <!-- 三个状态分别用方形（安装）、圆形（通信）、三角形（绑定）区分 -->
                        <div class="fee-meter-states">
                          <i
                            class="fee-meter-state state-install"
                            :class="meter.installed ? 'state-on' : 'state-off'"
                            :title="'安装状态：' + meter.install_status_name"
                          ></i>
                          <i
                            class="fee-meter-state state-comm"
                            :class="meter.comm_status === 'online' ? 'state-on' : 'state-off'"
                            :title="'通信状态：' + meter.comm_status_name"
                          ></i>
                          <i
                            class="fee-meter-state state-bind"
                            :class="meter.bound ? 'state-on' : 'state-off'"
                            :title="'绑定状态：' + meter.bind_status_name"
                          ></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="fee-meter-empty">暂无费控表数据</div>
              </div>
            </template>

            <!-- 其他类型节点（充电站/充电桩/充电枪/拐点等） -->
            <el-descriptions v-else-if="nodeInfo.uid" :column="1" border size="small">
              <el-descriptions-item label="节点名称">{{ nodeInfo.name || "-" }}</el-descriptions-item>
              <el-descriptions-item label="节点类型">{{ nodeInfo.node_type_name || "-" }}</el-descriptions-item>
              <el-descriptions-item label="节点ID">{{ nodeInfo.ID || "-" }}</el-descriptions-item>
              <el-descriptions-item label="资产码">{{ nodeInfo.asset_code || "-" }}</el-descriptions-item>
              <el-descriptions-item label="备注">{{ nodeInfo.remark || "-" }}</el-descriptions-item>
            </el-descriptions>

            <el-empty v-else description="请点击拓扑图中的节点查看详细信息" :image-size="80"></el-empty>

            <!-- 台区 / 一体柜 / 分支箱：费控统计（接口暂未返回该四项数据，先用前端自定义变量展示） -->
            <div v-if="showNodeFeeStat" class="node-fee-panel">
              <div class="node-fee-title">费控统计</div>
              <div class="node-fee-grid">
                <div v-for="item in nodeFeeItems" :key="item.key" class="node-fee-item">
                  <div class="node-fee-label">{{ item.label }}</div>
                  <div class="node-fee-value">
                    {{ formatStatValue(nodeFeeStat[item.key]) }}
                    <span class="node-fee-unit">{{ item.unit }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- 台区：KS 终端信息 -->
            <div v-if="nodeInfo.node_type === 'transformer'" class="ks-panel">
              <div class="ks-panel-header">
                <span class="ks-panel-title">
                  KS终端信息
                  <el-tag v-if="ksTerminalIsPlaceholder" size="mini" type="warning" class="ks-placeholder-tag">
                    示例数据
                  </el-tag>
                </span>
                <el-button type="text" size="mini" icon="el-icon-setting" @click="onOpenKsSetting" v-if="false">设置</el-button>
              </div>

              <template v-if="ksTerminal">
                <div class="ks-section-title">基本信息</div>
                <el-descriptions :column="1" border size="small">
                  <el-descriptions-item label="出厂编号">{{ ksTerminal.factory_no || "-" }}</el-descriptions-item>
                  <el-descriptions-item label="SIM卡号">{{ ksTerminal.sim_no || "-" }}</el-descriptions-item>
                  <el-descriptions-item label="充电桩软件版本号">{{ ksTerminal.pile_sw_version || "-" }}</el-descriptions-item>
                  <el-descriptions-item label="SIM卡网络运营商">{{ formatKsSimOperator(ksTerminal.sim_operator) }}</el-descriptions-item>
                  <el-descriptions-item label="SIM卡网络信号等级">{{ formatKsSignal(ksTerminal.sim_signal) }}</el-descriptions-item>
                  <el-descriptions-item label="实时监测上报频率">
                    {{ ksIntervalText(ksTerminal.report_interval) }}
                  </el-descriptions-item>
                  <el-descriptions-item label="负荷开关属性上报频率">
                    {{ ksIntervalText(ksTerminal.switch_report_interval) }}
                  </el-descriptions-item>
                </el-descriptions>

                <div class="ks-section-title">有序充电配置时段</div>
                <el-descriptions :column="1" border size="small">
                  <el-descriptions-item label="策略段数">
                    {{ ksTerminal.segments != null ? ksTerminal.segments + " 段" : "-" }}
                  </el-descriptions-item>
                  <el-descriptions-item label="策略优先级">{{ formatKsPriority(ksTerminal.priority) }}</el-descriptions-item>
                  <el-descriptions-item label="策略时间">
                    <div v-if="ksTerminal.times.length" class="ks-value-list">
                      <el-tag
                        v-for="(time, index) in ksTerminal.times"
                        :key="'time-' + index"
                        class="ks-value-tag"
                        size="mini"
                        type="info"
                      >
                        {{ formatKsTime(time) }}
                      </el-tag>
                    </div>
                    <span v-else>-</span>
                  </el-descriptions-item>
                  <el-descriptions-item label="策略配置功率">
                    <div v-if="ksTerminal.powers.length" class="ks-value-list">
                      <el-tag
                        v-for="(power, index) in ksTerminal.powers"
                        :key="'power-' + index"
                        class="ks-value-tag"
                        size="mini"
                      >
                        {{ formatKsPower(power) }}
                      </el-tag>
                    </div>
                    <span v-else>-</span>
                  </el-descriptions-item>
                </el-descriptions>
              </template>
              <div v-else class="ks-empty">暂无 KS 终端信息</div>
            </div>

            <!-- 三相监测曲线：第三层（一体柜/有序充电控制箱）、第四层（分支箱）节点 -->
            <div v-if="showPhaseCurvePanel" class="phase-curve-panel" v-loading="phaseMonitorLoading">
              <div class="phase-curve-title">
                三相监测曲线
                <span class="phase-curve-sub-title" v-if="phaseMonitorData">近 24 小时</span>
              </div>
              <template v-if="phaseMonitorData">
                <div class="phase-curve-item">
                  <div class="phase-curve-item-title">三相电压曲线（V）</div>
                  <div ref="phaseVoltageChart" class="phase-curve-chart"></div>
                </div>
                <div class="phase-curve-item">
                  <div class="phase-curve-item-title">三相电流曲线（A）</div>
                  <div ref="phaseCurrentChart" class="phase-curve-chart"></div>
                </div>
                <div class="phase-curve-item">
                  <div class="phase-curve-item-title">三相功率曲线（kW）</div>
                  <div ref="phasePowerChart" class="phase-curve-chart"></div>
                </div>
                <div class="phase-curve-item">
                  <div class="phase-curve-item-title">温度曲线（℃）</div>
                  <div ref="phaseTempChart" class="phase-curve-chart"></div>
                </div>
              </template>
              <div v-else class="phase-curve-empty">暂无三相监测数据</div>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <!-- KS 终端设置弹窗 -->
    <el-dialog
      title="KS终端设置"
      :visible.sync="ksSettingVisible"
      width="420px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-form :model="ksSettingForm" label-width="180px" size="small">
        <el-form-item label="实时监测上报频率">
          <el-input-number
            v-model="ksSettingForm.report_interval"
            :min="1"
            :max="86400"
            :step="10"
            controls-position="right"
          ></el-input-number>
          <span class="ks-form-unit">秒</span>
        </el-form-item>
        <el-form-item label="负荷开关属性上报频率">
          <el-input-number
            v-model="ksSettingForm.switch_report_interval"
            :min="1"
            :max="86400"
            :step="10"
            controls-position="right"
          ></el-input-number>
          <span class="ks-form-unit">秒</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="ksSettingVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="ksSettingSaving" @click="onSaveKsSetting">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import pageMixin from "@/mixins/pageMixin"
import * as echarts from "echarts"
import {
  getRegionTreeApi,
  getTqViewPageListApi,
  getTransformerOverviewStatisticApi,
  getTransformerTopoDataApi,
  getFeeTablelistApi,
  getFeeMeterMonitorDataApi,
  getAcHisListApi,
  getTopoNodeItemDetailApi,
  saveKsTerminalConfigApi
} from "@/api/dev_archive"

// 设备类型（dev_type）与内部节点类型标识的映射
// 0=充电站、1=台区、2=一体柜、3=分支箱、4=充电桩、5=充电枪、6=拐点、7=国网电表箱
const TOPO_DEV_TYPE_KEYS = {
  0: "site",
  1: "transformer",
  2: "cabinet",
  3: "branch_box",
  4: "pile",
  5: "gun",
  6: "turn",
  7: "meter_box"
}

// 节点类型标识 → 设备类型（dev_type），节点未返回 dev_type 时用于拼装节点详情接口参数
const TOPO_NODE_TYPE_DEV_TYPES = {
  site: 0,
  transformer: 1,
  cabinet: 2,
  control_box: 2,
  branch_box: 3,
  pile: 4,
  gun: 5,
  turn: 6,
  meter_box: 7
}

// 拓扑各层节点颜色
const TOPO_NODE_COLORS = {
  transformer: "#409EFF", // 台区
  meter_box: "#67C23A", // 国网电表箱
  cabinet: "#E6A23C", // 一体柜
  control_box: "#E6A23C", // 有序充电控制箱
  branch_box: "#F56C6C", // 分支箱
  pile: "#909399", // 充电桩
  gun: "#909399", // 充电枪
  site: "#909399", // 充电站
  turn: "#909399", // 拐点
  unknown: "#909399"
}

// 拓扑各层节点尺寸
const TOPO_NODE_SIZES = {
  transformer: 16,
  meter_box: 13,
  cabinet: 13,
  control_box: 13,
  branch_box: 10
}

// 拓扑层数（台区变压器 → 国网电表箱 → 有序充电控制箱 → 分支箱）
const TOPO_LAYER_HEIGHT = 165

// 拓扑图每个可见叶子节点的横向宽度（画布宽度 = 可见叶子数 × 该宽度）
// 分支箱名称后带路数（如：演示分支箱（18路）），宽度适当放大避免节点文字重叠
const TOPO_LEAF_WIDTH = 120

// KS 终端：SIM 卡网络运营商（10 未成功获取、11 联通、12 移动、13 电信、14 其他运营商）
const KS_SIM_OPERATORS = {
  10: "未成功获取",
  11: "联通",
  12: "移动",
  13: "电信",
  14: "其他运营商"
}

// KS 终端：有序充电策略优先级
const KS_PRIORITY_TYPES = {
  10: "预测调控计划",
  15: "动态调控",
  20: "邀约调控计划",
  25: "特殊调控"
}

// KS 终端：上报频率默认值（单位：秒）
const KS_DEFAULT_REPORT_INTERVAL = 300

// 数据统计示例数据（统计接口暂未实现/接口异常时用于界面展示）
const STATISTIC_DEMO_DATA = {
  total_count: 128, // 台区总数
  cabinet_count: 96, // 一体柜数量
  branch_box_count: 384, // 分支箱数量
  fee_meter_capacity: 5760, // 费控表容量（路）
  installed_fee_meter_count: 5124, // 已装费控表数
  bound_user_count: 8960, // 已绑用户
  remain_bind_user_count: 684 // 剩余可绑用户
}

// 节点费控统计示例数据（接口暂未返回该四项数据，先按节点类型在前端自定义占位变量）
// 字段：fee_meter_capacity 费控表容量、installed_fee_meter_count 已装费控表数、
//       bound_user_count 已绑用户、remain_bind_user_count 剩余可绑用户
const NODE_FEE_STAT_DEMO = {
  transformer: {
    fee_meter_capacity: 180,
    installed_fee_meter_count: 168,
    bound_user_count: 336,
    remain_bind_user_count: 24
  },
  cabinet: {
    fee_meter_capacity: 36,
    installed_fee_meter_count: 34,
    bound_user_count: 68,
    remain_bind_user_count: 4
  },
  branch_box: {
    fee_meter_capacity: 18,
    installed_fee_meter_count: 17,
    bound_user_count: 34,
    remain_bind_user_count: 2
  }
}

// KS 终端示例数据（仅在 showKsTerminalDemo 开启、且接口未返回 ks_terminal 时用于界面展示）
const KS_TERMINAL_PLACEHOLDER = {
  basic_info: {
    factory_no: "KS202610200001",
    sim_no: "898604A1234567890123",
    pile_sw_version: "V1.2.0",
    sim_operator: 12,
    sim_signal: 23
  },
  order_charge: {
    // unix 时间戳（精确到秒）：2026-10-20 08:00 / 11:00 / 18:20
    times: [1792454400, 1792465200, 1792491600],
    powers: [30.5, 45, 22],
    num: 3,
    priority: 15
  },
  report_interval: 300,
  switch_report_interval: 300
}

export default {
  name: "TransformerOverview",
  mixins: [pageMixin],
  data() {
    return {
      // 区域级联层级（省 → 市 → 区/县 → 街道 → 社区 → 站点，level 与接口保持一致）
      areaLevels: [
        { level: 0, key: "province_id", label: "省" },
        { level: 1, key: "city_id", label: "市" },
        { level: 2, key: "county_id", label: "区/县" },
        { level: 3, key: "street_id", label: "街道" },
        { level: 4, key: "community_id", label: "社区" },
        { level: 5, key: "estate_id", label: "站点" }
      ],
      filter: {
        province_id: "",
        city_id: "",
        county_id: "",
        street_id: "",
        community_id: "",
        estate_id: ""
      },
      // 区域级联树（接口一次性返回）与各级下拉选项（下标即 level）
      regionTree: [],
      regionOptions: [[], [], [], [], [], []],
      // 统计数据（统计当前搜索条件下的台区及设备数据，初始展示示例数据）
      statistic: Object.assign({}, STATISTIC_DEMO_DATA),
      statisticLoading: false,
      // 当前统计数据是否来自示例数据（接口未实现/异常时兜底展示示例数据）
      statisticIsDemo: true,
      statCards: [
        { label: "台区总数", valueKey: "total_count", unit: "个", colorClass: "stat-blue", primary: true },
        { label: "一体柜数量", valueKey: "cabinet_count", unit: "台", colorClass: "stat-cyan" },
        { label: "分支箱数量", valueKey: "branch_box_count", unit: "台", colorClass: "stat-green" },
        { label: "费控表容量", valueKey: "fee_meter_capacity", unit: "路", colorClass: "stat-purple" },
        { label: "已装费控表数", valueKey: "installed_fee_meter_count", unit: "只", colorClass: "stat-orange" },
        { label: "已绑用户", valueKey: "bound_user_count", unit: "户", colorClass: "stat-indigo" },
        { label: "剩余可绑用户", valueKey: "remain_bind_user_count", unit: "户", colorClass: "stat-red" }
      ],
      // 列表数据
      tableData: [],
      loading: false,
      // 拓扑数据
      topoVisible: false,
      topoLoading: false,
      currentTransformer: {},
      topoData: null,
      nodeInfo: {},
      selectedNodeUid: "",
      topoChartWidth: 800,
      topoChartHeight: TOPO_LAYER_HEIGHT * 4,
      // 聚焦的节点（点击电表箱/一体柜后，同层其他节点收缩），使用节点 uid 标识（电表箱可能未录入电表号，id 为空）
      hasFocus: false,
      focusLevel2Uid: "",
      focusLevel3Uid: "",
      topoLegends: [
        { type: "transformer", label: "台区", color: TOPO_NODE_COLORS.transformer },
        { type: "meter_box", label: "国网电表箱", color: TOPO_NODE_COLORS.meter_box },
        { type: "cabinet", label: "一体柜", color: TOPO_NODE_COLORS.cabinet },
        { type: "branch_box", label: "分支箱", color: TOPO_NODE_COLORS.branch_box }
      ],
      // 右侧信息区视图：node=节点信息、meter=费控表监测数据
      infoView: "node",
      // 节点费控统计（费控表容量/已装费控表数/已绑用户/剩余可绑用户）
      // 说明：接口暂未返回该四项数据，先由前端自定义变量占位展示
      nodeFeeStat: Object.assign({}, NODE_FEE_STAT_DEMO.transformer),
      nodeFeeItems: [
        { key: "fee_meter_capacity", label: "费控表容量", unit: "路" },
        { key: "installed_fee_meter_count", label: "已装费控表数", unit: "只" },
        { key: "bound_user_count", label: "已绑用户", unit: "户" },
        { key: "remain_bind_user_count", label: "剩余可绑用户", unit: "户" }
      ],
      // 节点详情接口请求状态（详情优先取接口返回，缺失字段沿用拓扑节点数据）
      nodeDetailLoading: false,
      nodeDetailSeq: 0,
      // 是否展示 KS 终端示例数据（默认关闭；开启后接口未返回 ks_terminal 时用示例数据展示界面）
      showKsTerminalDemo: true,
      // KS 终端设置弹窗
      ksSettingVisible: false,
      ksSettingSaving: false,
      ksSettingForm: {
        report_interval: KS_DEFAULT_REPORT_INTERVAL,
        switch_report_interval: KS_DEFAULT_REPORT_INTERVAL
      },
      currentMeter: {},
      meterMonitorData: null,
      meterLoading: false,
      // 分支箱下的通道/费控表数据（路数由接口返回决定，按通道号动态划分 A/B/C 相）
      feeTableList: [],
      feeTableLoading: false,
      // 三相监测数据（A/B/C 三相电压/电流/功率 + 温度曲线）：第三层一体柜/控制箱、第四层分支箱共用
      phaseMonitorData: null,
      phaseMonitorLoading: false,
      // 三相监测请求序号（切换节点时丢弃过期响应）
      phaseMonitorSeq: 0,
      // 费控表相位分组
      feeMeterPhases: [
        { key: "A", label: "A相" },
        { key: "B", label: "B相" },
        { key: "C", label: "C相" }
      ]
    }
  },
  computed: {
    // 统计范围提示：展示当前搜索条件中各级已选区域
    statisticScopeText() {
      const names = []
      this.areaLevels.forEach((item, index) => {
        const value = this.filter[item.key]
        if (!value) return
        const option = (this.regionOptions[index] || []).find((opt) => opt.id === value)
        names.push(`${item.label}：${option ? option.Text : value}`)
      })
      return names.length ? `统计范围 ${names.join(" / ")}` : "统计范围：全部区域"
    },
    nodeInfoTitle() {
      const isTopNode = this.topoData && this.nodeInfo && this.nodeInfo.uid === this.topoData.uid
      return isTopNode ? "顶级节点信息" : "节点信息"
    },
    // 是否需要展示三相监测曲线（第三层一体柜/控制箱、第四层分支箱）
    showPhaseCurvePanel() {
      const nodeType = this.nodeInfo && this.nodeInfo.node_type
      return this.isLevel3Type(nodeType) || nodeType === "branch_box"
    },
    // 是否需要展示节点费控统计（台区、一体柜/控制箱、分支箱）
    showNodeFeeStat() {
      const nodeType = this.nodeInfo && this.nodeInfo.node_type
      return nodeType === "transformer" || this.isLevel3Type(nodeType) || nodeType === "branch_box"
    },
    // KS 终端信息：优先使用接口数据；接口未返回时，仅在开启示例数据开关后使用示例数据
    ksTerminal() {
      const ksTerminal = (this.nodeInfo && this.nodeInfo.ks_terminal) || null
      if (ksTerminal) return ksTerminal
      return this.showKsTerminalDemo ? this.normalizeKsTerminal(KS_TERMINAL_PLACEHOLDER) : null
    },
    // 当前展示的 KS 终端信息是否来自示例数据
    ksTerminalIsPlaceholder() {
      return !(this.nodeInfo && this.nodeInfo.ks_terminal) && this.showKsTerminalDemo
    }
  },
  created() {
    // 三相曲线图表实例（不放入 data，避免被响应式劫持）
    this.phaseCharts = {}
    this.loadRegionTree()
    this.getStatistic()
    this.getData()
  },
  mounted() {
    window.addEventListener("resize", this.onWindowResize)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onWindowResize)
    if (this.topoChart) {
      this.topoChart.dispose()
      this.topoChart = null
    }
    this.disposeMeterChart()
    this.disposePhaseCharts()
  },
  methods: {
    /* ============ 区域级联（一次性获取区域树，本地按层级级联） ============ */
    loadRegionTree() {
      getRegionTreeApi().then((res) => {
        const tree = (res && (res.tree || (res.output && res.output.tree))) || []
        this.regionTree = Array.isArray(tree) ? tree : []
        this.buildRegionOptions()
      }).catch(() => {
        // 接口异常时不提供区域选项
        this.regionTree = []
        this.buildRegionOptions()
      })
    },

    // 根据各级当前选中值，逐级下钻生成每级下拉选项
    buildRegionOptions() {
      const options = this.areaLevels.map(() => [])
      let currentList = this.regionTree || []
      options[0] = currentList
      for (let i = 1; i < this.areaLevels.length; i++) {
        const parentKey = this.areaLevels[i - 1].key
        const parentId = this.filter[parentKey]
        const parentNode = parentId ? currentList.find((node) => node.id === parentId) : null
        currentList = (parentNode && parentNode.children) || []
        options[i] = currentList
      }
      this.regionOptions = options
    },

    // 上级未选择（或选择了全部）时，下级不可查询
    isAreaDisabled(level) {
      if (level <= 0) return false
      const parentKey = this.areaLevels[level - 1].key
      return !this.filter[parentKey]
    },

    onAreaChange(level) {
      // 清空下级（选择"全部"后不能继续级联查询），下级将因上级为空而禁用
      for (let i = level + 1; i < this.areaLevels.length; i++) {
        this.filter[this.areaLevels[i].key] = ""
      }
      this.buildRegionOptions()
    },

    onResetFilter() {
      this.areaLevels.forEach((item) => {
        this.filter[item.key] = ""
      })
      this.buildRegionOptions()
      this.toFilter()
    },

    // 当前选中的最深层级区域（接口参数：level + region_id）
    getCurrentRegionParam() {
      let level = null
      let regionId = ""
      this.areaLevels.forEach((item) => {
        const value = this.filter[item.key]
        if (value) {
          level = item.level
          regionId = value
        }
      })
      return { level, region_id: regionId }
    },

    // 覆盖 pageMixin 的 toFilter：按搜索条件查询时同步刷新统计数据
    toFilter() {
      this.pageParams.page = 1
      this.getStatistic()
      this.getData()
    },

    /* ============ 数据统计 ============ */
    // 组装查询参数：选中区域的 level + region_id（未选择任何区域时不传）
    buildSearchParams() {
      const params = {}
      const region = this.getCurrentRegionParam()
      if (region.level !== null && region.region_id) {
        params.level = region.level
        params.region_id = region.region_id
      }
      return params
    },

    // 统一取数：兼容 { data: [] }、{ outputData: {...} }、{ output: {...} }、{ list: [] } 等返回结构
    pickApiPayload(res) {
      if (!res) return {}
      if (res.outputData) return res.outputData
      if (res.output) return res.output
      if (res.data && !Array.isArray(res.data)) return res.data
      return res
    },

    // 获取当前搜索条件下的统计数据
    getStatistic() {
      const params = this.buildSearchParams()
      /* this.statisticLoading = true
      getTransformerOverviewStatisticApi(params).then((res) => {
        const data = this.pickApiPayload(res)
        // 接口未返回有效统计数据时（当前为约定的假路径），兜底展示示例数据
        if (!data || !this.hasStatisticData(data)) {
          this.setStatistic(STATISTIC_DEMO_DATA, true)
          return
        }
        this.setStatistic(data, false)
      }).catch(() => {
        // 接口异常时展示示例数据
        this.setStatistic(STATISTIC_DEMO_DATA, true)
      }).finally(() => {
        this.statisticLoading = false
      }) */
      // 接口异常时展示示例数据
      this.setStatistic(STATISTIC_DEMO_DATA, true)
    },

    // 接口返回的统计数据中任一统计项有值即视为有效
    hasStatisticData(data) {
      return this.statCards.some((item) => {
        const val = data[item.valueKey]
        return val !== null && val !== undefined && val !== ""
      })
    },

    setStatistic(data, isDemo) {
      const source = data || {}
      const statistic = {}
      this.statCards.forEach((item) => {
        const val = Number(source[item.valueKey])
        statistic[item.valueKey] = isNaN(val) ? 0 : val
      })
      this.statistic = statistic
      this.statisticIsDemo = !!isDemo
    },

    // 统计值展示：千分位分隔（空值展示 0）
    formatStatValue(val) {
      const num = Number(val)
      if (val === null || val === undefined || val === "" || isNaN(num)) return "0"
      return String(Math.round(num)).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    /* ============ 台区列表 ============ */
    getData() {
      this.loading = true
      const params = {
        page: this.pageParams.page,
        pagesize: this.pageParams.limit,
        ...this.buildSearchParams()
      }
      getTqViewPageListApi(params).then((res) => {
        const output = this.pickApiPayload(res)
        this.tableData = output.data || output.list || []
        this.pageParams.total = output.total || output.count || 0
      }).catch(() => {
        // 接口异常时不展示列表数据
        this.tableData = []
        this.pageParams.total = 0
      }).finally(() => {
        this.loading = false
      })
    },

    statusTagType(status) {
      if (status === "normal") return "success"
      if (status === "ordered") return "warning"
      if (status === "limit") return "danger"
      if (status === "warning") return "warning"
      if (status === "offline") return "info"
      return "info"
    },

    formatAmount(val) {
      if (val === null || val === undefined || val === "") return "-"
      const num = Number(val)
      if (isNaN(num)) return val
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    /* ============ 拓扑数据加载 ============ */
    onViewTopo(row) {
      this.currentTransformer = { ...row }
      this.topoVisible = true
      this.topoLoading = true
      this.topoData = null
      this.nodeInfo = {}
      this.selectedNodeUid = ""
      this.infoView = "node"
      this.currentMeter = {}
      this.meterMonitorData = null
      this.feeTableList = []
      this.phaseMonitorData = null
      this.phaseMonitorSeq = 0
      this.nodeDetailLoading = false
      this.nodeDetailSeq = 0
      this.disposeMeterChart()
      this.disposePhaseCharts()
      // 聚焦状态待拓扑数据加载完成后初始化（默认展开第二层最左边的分支）
      this.hasFocus = false
      this.focusLevel2Uid = ""
      this.focusLevel3Uid = ""
      getTransformerTopoDataApi({ transformer_id: row.ID }).then((res) => {
        // 接口返回结构：{ output: null, outputData: { 台区节点树 } }，统一取数
        this.setTopoData(this.pickApiPayload(res) || null)
      }).catch(() => {
        // 接口异常时不展示拓扑数据
        this.setTopoData(null)
        this.$message.error("拓扑数据加载失败")
      }).finally(() => {
        this.topoLoading = false
      })
    },

    setTopoData(topoData) {
      // 统一转换为内部节点结构（兼容接口的 id/label/dev_type/type 与节点字段）
      this.topoData = this.normalizeTopoNode(topoData, "", 0)
      // 默认展示拓扑图顶级节点（台区）信息
      this.nodeInfo = this.topoData || {}
      // 顶级节点（台区）费控统计
      this.setNodeFeeStatistic(this.nodeInfo)
      // 动态获取顶级节点详情
      this.fetchNodeDetail(this.topoData)
      // 初始展开第二层最左边的分支（第一个国网电表箱及其下所有节点），其余两个收缩
      this.resetTopoFocus(this.topoData)
      this.$nextTick(() => {
        this.renderTopoChart()
        const topoCard = this.$el.querySelector(".topo-card")
        if (topoCard && topoCard.scrollIntoView) {
          topoCard.scrollIntoView({ behavior: "smooth", block: "start" })
        }
      })
    },

    // 初始聚焦状态：
    // 1. 第二层有 3 个节点时，展开第二个节点及其下所有节点，其余收缩；
    // 2. 第二层少于 3 个节点时，展开最右边（最后一个）节点及其下所有节点，其余收缩
    resetTopoFocus(topoData) {
      const meterBoxList = (topoData && topoData.children) || []
      if (!meterBoxList.length) {
        this.hasFocus = false
        this.focusLevel2Uid = ""
        this.focusLevel3Uid = ""
        return
      }
      const focusIndex = meterBoxList.length >= 3 ? 1 : meterBoxList.length - 1
      const focusMeterBox = meterBoxList[focusIndex]
      const firstLevel3Node = (focusMeterBox && focusMeterBox.children && focusMeterBox.children[0]) || null
      this.hasFocus = true
      this.focusLevel2Uid = focusMeterBox ? focusMeterBox.uid : ""
      this.focusLevel3Uid = firstLevel3Node ? firstLevel3Node.uid : ""
    },

    // 接口数据（id/label/type/dev_type/children）统一转换为内部节点结构
    // 说明：国网电表箱在未录入电表号时 id 为空，此处用 triplet_id 或父级路径生成唯一 uid，保证折叠/查找可用
    normalizeTopoNode(rawNode, parentUid, index) {
      if (!rawNode) return null
      const devType = rawNode.dev_type != null && rawNode.dev_type !== "" ? Number(rawNode.dev_type) : null
      const nodeType = devType != null
        ? TOPO_DEV_TYPE_KEYS[devType] || "unknown"
        : rawNode.node_type || "unknown"
      const rawId = rawNode.id != null && rawNode.id !== "" ? String(rawNode.id) : ""
      const tripletId = rawNode.triplet_id != null && rawNode.triplet_id !== "" ? String(rawNode.triplet_id) : ""
      const uid = rawId || `${parentUid || "root"}-${tripletId || index}`
      const rawChildren = rawNode.children || []
      const children = rawChildren
        .map((child, childIndex) => this.normalizeTopoNode(child, uid, childIndex))
        .filter((child) => !!child)

      return {
        uid,
        // 原始 id（电表箱未录入电表号时为空字符串）
        ID: rawId,
        triplet_id: tripletId,
        name: rawNode.label || rawNode.Text || rawNode.name || "-",
        node_type: nodeType,
        node_type_name: rawNode.type || rawNode.node_type_name || "-",
        dev_type: devType,
        asset_code: rawNode.assets_code || rawNode.asset_code || "",
        status: rawNode.status || "",
        status_name: rawNode.status_name || "",
        // 第三层（一体柜/有序充电控制箱）
        switch_status: rawNode.switch_status || "",
        charge_status: rawNode.charge_status || "",
        copper_temp: rawNode.copper_temp,
        terminal: rawNode.terminal || null,
        // 第四层（分支箱）
        box_temp: rawNode.box_temp,
        door_status: rawNode.door_status || "",
        sim_no: rawNode.sim_no || "",
        sim_expire: rawNode.sim_expire || "",
        // 分支箱路数（拓扑图中分支箱名称后展示，如：演示分支箱（18路））
        branch_box_gun_no:
          rawNode.branch_box_gun_no !== null && rawNode.branch_box_gun_no !== undefined
            ? rawNode.branch_box_gun_no
            : "",
        fee_meters: rawNode.fee_meters || [],
        // 其他
        region_name: rawNode.region_name || "",
        create_time: rawNode.create_time || "",
        remark: rawNode.remark || rawNode.tips || rawNode.short_tips || "",
        children
      }
    },

    // 拓扑节点显示名称：分支箱在名称后追加路数（branch_box_gun_no），路数为空时不追加
    formatNodeLabel(node) {
      if (!node) return "-"
      const name = node.name || "-"
      if (node.node_type !== "branch_box") return name
      const gunNo = node.branch_box_gun_no
      if (gunNo === null || gunNo === undefined || gunNo === "") return name
      const count = Number(gunNo)
      if (isNaN(count)) return name
      return `${name}（${count}路）`
    },

    // 节点费控统计：接口暂未返回该四项数据，先按节点类型取前端自定义占位数据
    setNodeFeeStatistic(node) {
      const nodeType = (node && node.node_type) || "transformer"
      const demo = NODE_FEE_STAT_DEMO[nodeType] || NODE_FEE_STAT_DEMO.transformer
      const stat = Object.assign({}, demo)
      // 分支箱：费控表容量优先取接口返回的路数
      if (nodeType === "branch_box") {
        const gunNo = Number(node && node.branch_box_gun_no)
        if (!isNaN(gunNo) && gunNo > 0) {
          stat.fee_meter_capacity = gunNo
          stat.installed_fee_meter_count = Math.min(stat.installed_fee_meter_count, gunNo)
        }
      }
      this.nodeFeeStat = stat
    },

    /* ============ 拓扑图渲染 ============ */
    // 画布宽度：不小于可视宽度，且保证所有可见叶子节点完整展示（不足时横向滚动）
    calcTopoChartWidth() {
      const scrollDom = this.$refs.topoScroll
      const minWidth = scrollDom ? scrollDom.clientWidth : 800
      const leafCount = this.countVisibleLeaf(this.topoData)
      return Math.max(minWidth - 4, leafCount * TOPO_LEAF_WIDTH)
    },

    renderTopoChart() {
      if (!this.topoData || !this.$refs.topoChart) return
      const chartWidth = this.calcTopoChartWidth()
      this.topoChartWidth = chartWidth
      this.topoChartHeight = TOPO_LAYER_HEIGHT * 4

      this.$nextTick(() => {
        if (!this.$refs.topoChart) return
        // 图表实例挂在组件实例上（不放入 data，避免被响应式劫持）
        // 容器被重建时（收起后重新打开）销毁旧实例
        if (this.topoChart && this.topoChart.getDom() !== this.$refs.topoChart) {
          this.topoChart.dispose()
          this.topoChart = null
        }
        if (!this.topoChart) {
          this.topoChart = echarts.init(this.$refs.topoChart)
          this.topoChart.on("click", this.onTopoNodeClick)
        }
        this.topoChart.clear()
        this.topoChart.setOption(this.buildTopoOption(this.topoData), true)
        this.topoChart.resize({ width: chartWidth, height: this.topoChartHeight })
      })
    },

    // 从上往下（四层）的树形拓扑
    buildTopoOption(rootNode) {
      return {
        tooltip: {
          trigger: "item",
          triggerOn: "mousemove",
          formatter: (params) => {
            const data = (params && params.data) || {}
            return `${data.name || "-"}<br/>类型：${data.node_type_name || "-"}<br/>状态：${data.status_name || "-"}`
          }
        },
        series: [
          {
            type: "tree",
            data: [this.buildTopoChartNode(rootNode)],
            left: 40,
            right: 40,
            top: 30,
            bottom: 30,
            layout: "orthogonal",
            orient: "TB", // 从上往下
            symbol: "circle",
            expandAndCollapse: false, // 折叠状态由业务逻辑控制
            initialTreeDepth: -1,
            roam: false,
            lineStyle: { color: "#cbd5e1", width: 1.5, curveness: 0.5 },
            label: {
              position: "bottom",
              align: "center",
              verticalAlign: "top",
              distance: 6,
              fontSize: 12,
              color: "#606266"
            },
            emphasis: { focus: "descendant" }
          }
        ]
      }
    },

    // 转换为图表节点（按层着色、标记选中；收缩的节点不生成子节点，以此实现展开/收缩）
    buildTopoChartNode(node) {
      if (!node) return null
      const isSelected = this.selectedNodeUid === node.uid
      const data = {
        uid: node.uid,
        ID: node.ID,
        // 分支箱名称后追加路数，如：演示分支箱（18路）
        name: this.formatNodeLabel(node),
        node_type: node.node_type,
        node_type_name: node.node_type_name,
        status_name: node.status_name,
        symbolSize: TOPO_NODE_SIZES[node.node_type] || 10,
        itemStyle: {
          color: TOPO_NODE_COLORS[node.node_type] || "#909399",
          borderColor: isSelected ? "#303133" : "#ffffff",
          borderWidth: isSelected ? 3 : 1.5
        }
      }
      if (node.children && node.children.length && !this.isNodeCollapsed(node)) {
        data.children = node.children.map((child) => this.buildTopoChartNode(child))
      }
      return data
    },

    // 是否为第三层节点类型（一体柜 / 有序充电控制箱）
    isLevel3Type(nodeType) {
      return nodeType === "cabinet" || nodeType === "control_box"
    },

    // 是否为第三层节点
    isLevel3Node(node) {
      return !!node && this.isLevel3Type(node.node_type)
    },

    // 第二层/第三层节点：非聚焦节点收缩（同层只展开一个）
    isNodeCollapsed(node) {
      if (!this.hasFocus || !node) return false
      if (node.node_type === "meter_box") return node.uid !== this.focusLevel2Uid
      if (this.isLevel3Node(node)) return node.uid !== this.focusLevel3Uid
      return false
    },

    // 统计当前可见的叶子节点数量（用于计算画布宽度）
    countVisibleLeaf(node) {
      if (!node) return 0
      if (this.isNodeCollapsed(node)) return 1
      if (!node.children || !node.children.length) return 1
      return node.children.reduce((sum, child) => sum + this.countVisibleLeaf(child), 0)
    },

    // 点击拓扑节点：右侧展示该节点信息；第二/三层节点连带其下节点展开，同层其他节点收缩
    onTopoNodeClick(params) {
      if (!params) return
      // tree 系列的点击事件 params.dataType 不能作为判据，此处以节点 uid 优先、名称兜底定位节点
      const clickedUid = params.data && params.data.uid
      let node = clickedUid ? this.findTopoNode(this.topoData, clickedUid) : null
      if (!node && params.name) {
        node = this.findTopoNodeByName(this.topoData, params.name)
      }
      if (!node) return
      this.selectedNodeUid = node.uid
      this.nodeInfo = node
      // 当前节点的费控统计（台区/一体柜/分支箱）
      this.setNodeFeeStatistic(node)
      // 动态获取该节点的详情数据（整个节点对象作为请求参数）
      this.fetchNodeDetail(node)
      // 若当前处于费控表监测视图，先销毁图表再切回节点信息视图
      this.disposeMeterChart()
      // 切换节点后三相曲线容器会被重建，先销毁旧实例避免残留
      this.disposePhaseCharts()
      this.infoView = "node"
      this.currentMeter = {}
      this.meterMonitorData = null
      // 切换节点时先清空上一个节点的三相监测数据
      this.phaseMonitorData = null
      this.phaseMonitorSeq += 1
      this.phaseMonitorLoading = false
      if (node.node_type === "branch_box") {
        // 分支箱：获取其下通道与费控表数据
        this.fetchFeeTableList(node)
      } else {
        this.feeTableList = []
      }
      // 分支箱 / 一体柜（有序充电控制箱）：请求三相监测数据
      // 拓扑树节点未返回资产码，此时由节点详情接口返回的资产码触发请求
      if (node.node_type === "branch_box" || this.isLevel3Node(node)) {
        this.fetchPhaseMonitorIfNeeded(node)
      }
      this.focusTopoNode(node)
      this.renderTopoChart()
    },

    /* ============ 节点详情（点击节点动态获取） ============ */
    // 以整个节点对象为参数请求节点详情，返回数据合并到 nodeInfo 展示（接口异常时沿用拓扑节点数据）
    fetchNodeDetail(node) {
      if (!node) return
      const params = this.buildNodeDetailParams(node)
      // 设备 ID 为空（如未录入电表号的电表箱）时服务端无法按 ID 查询，直接使用拓扑节点数据
      if (!params.id) {
        this.nodeDetailSeq += 1
        this.nodeDetailLoading = false
        return
      }
      // 请求序号：快速连续点击节点时丢弃过期响应
      const seq = this.nodeDetailSeq + 1
      this.nodeDetailSeq = seq
      this.nodeDetailLoading = true
      getTopoNodeItemDetailApi(params).then((res) => {
        if (seq !== this.nodeDetailSeq) return
        const payload = this.pickApiPayload(res)
        const resp = payload && payload.resp
        if (!resp) return
        // 仅在当前展示的仍是该节点时覆盖，避免串数据
        if (this.nodeInfo && this.nodeInfo.uid === node.uid) {
          this.nodeInfo = this.mergeNodeDetail(node, resp)
          // 详情可能返回分支箱路数，同步刷新费控统计
          this.setNodeFeeStatistic(this.nodeInfo)
          // 拓扑树节点不含资产码，拿到详情中的资产码后再请求三相监测数据
          this.fetchPhaseMonitorIfNeeded(this.nodeInfo)
        }
      }).catch(() => {
        // 接口异常时保留拓扑节点自身数据，不打断页面展示
      }).finally(() => {
        if (seq === this.nodeDetailSeq) {
          this.nodeDetailLoading = false
        }
      })
    },

    // 节点详情请求参数：整个节点对象 + 服务端模型字段（id/label/Text/type/dev_type/assets_code/triplet_id）
    buildNodeDetailParams(node) {
      const params = Object.assign({}, node, {
        id: node.ID || "",
        label: node.name || "",
        Text: node.name || "",
        type: node.node_type_name || "",
        dev_type: this.getNodeDevType(node),
        // 服务端按三元组编号查询资产码，这里不能进行数值类型的转换，不然会出错
        triplet_id: node.triplet_id,
        assets_code: node.asset_code || ""
      })
      // 下级节点与费控表列表无需传给详情接口，避免把整棵子树带到服务端
      delete params.children
      delete params.fee_meters
      return params
    },

    // 节点对应的设备类型（dev_type）：优先使用拓扑节点返回值，缺失时按节点类型推断
    getNodeDevType(node) {
      if (!node) return null
      if (node.dev_type !== null && node.dev_type !== undefined && node.dev_type !== "") {
        return Number(node.dev_type)
      }
      const devType = TOPO_NODE_TYPE_DEV_TYPES[node.node_type]
      return devType === undefined ? null : devType
    },

    // 合并接口返回的节点实体与实时交流数据：接口字段优先，缺失时沿用拓扑节点数据
    mergeNodeDetail(node, resp) {
      const detail = Object.assign({}, node)
      const entity = resp.transformer || resp.cabinet || resp.branch_box || null
      const triplet = resp.triplet || null
      // 实时数据：新接口返回 plat_form，兼容旧的 ac_data 结构
      const platForm = resp.plat_form || null
      const acData = resp.ac_data || platForm || null

      if (entity) {
        if (entity.ID != null) detail.ID = String(entity.ID)
        if (entity.name) detail.name = entity.name
        if (entity.assets_code) detail.asset_code = entity.assets_code
        if (entity.remark) detail.remark = entity.remark
        if (entity.address) detail.address = entity.address
        if (entity.create_time) detail.create_time = entity.create_time
        if (entity.is_operation !== null && entity.is_operation !== undefined) {
          detail.is_operation = entity.is_operation
        }
        // 一体柜独有字段
        if (entity.branch_box_nums != null) detail.branch_box_nums = entity.branch_box_nums
        if (entity.elect_tb_no) detail.elect_tb_no = entity.elect_tb_no
        if (entity.ct_no) detail.ct_no = entity.ct_no
        // 分支箱独有字段
        if (entity.power_cabinet_id != null) detail.power_cabinet_id = entity.power_cabinet_id
        if (entity.charging_config_id != null) detail.charging_config_id = entity.charging_config_id
        // 分支箱路数（拓扑图中分支箱名称后展示的路数）
        if (entity.branch_box_gun_no != null && entity.branch_box_gun_no !== "") {
          detail.branch_box_gun_no = entity.branch_box_gun_no
        }
      }

      // 资产码以三元组（bmi_triplet.assetsCode）为准，其次取实时缓存（dev_plat_form_cache）中的资产码
      const assetsCode = (triplet && triplet.assetsCode) || (platForm && platForm.assets_code)
      if (assetsCode) detail.asset_code = assetsCode

      if (acData) {
        // 设备上报值为放大值，按既有口径还原：温度 /100、电量 /1000
        const gunTemp = this.toFixedNumber(acData.gunTemp, 100, 1)
        if (this.isLevel3Type(detail.node_type)) {
          detail.copper_temp = gunTemp != null ? gunTemp : detail.copper_temp
        } else if (detail.node_type === "branch_box") {
          detail.box_temp = gunTemp != null ? gunTemp : detail.box_temp
        }
        const totalElect = this.toFixedNumber(acData.totalElect, 1000, 2)
        if (totalElect != null) detail.total_elect = totalElect
        detail.ac_update_time = acData.create_time || ""
      }

      // KS 终端信息（台区）：服务端返回 resp.ks_terminal，字段结构由前端先行约定
      if (resp.ks_terminal && Object.keys(resp.ks_terminal).length) {
        detail.ks_terminal = this.normalizeKsTerminal(resp.ks_terminal)
      }
      return detail
    },

    /* ============ KS 终端信息（台区） ============ */
    // 统一 KS 终端数据结构，缺失字段给出默认值（接口字段约定：
    // { basic_info: { factory_no, sim_no, pile_sw_version, sim_operator, sim_signal },
    //   order_charge: { times: [unix秒], powers: [kW], num, priority },
    //   report_interval, switch_report_interval }）
    normalizeKsTerminal(source) {
      const raw = source || {}
      const basic = raw.basic_info || {}
      const orderCharge = raw.order_charge || {}
      const times = Array.isArray(orderCharge.times) ? orderCharge.times : []
      const powers = Array.isArray(orderCharge.powers) ? orderCharge.powers : []
      const segments = orderCharge.num != null ? Number(orderCharge.num) : times.length
      return {
        factory_no: basic.factory_no || "",
        sim_no: basic.sim_no || "",
        pile_sw_version: basic.pile_sw_version || "",
        sim_operator: basic.sim_operator != null ? Number(basic.sim_operator) : null,
        sim_signal: basic.sim_signal != null ? Number(basic.sim_signal) : null,
        times,
        powers,
        segments,
        priority: orderCharge.priority != null ? Number(orderCharge.priority) : null,
        report_interval: raw.report_interval != null ? Number(raw.report_interval) : KS_DEFAULT_REPORT_INTERVAL,
        switch_report_interval:
          raw.switch_report_interval != null ? Number(raw.switch_report_interval) : KS_DEFAULT_REPORT_INTERVAL
      }
    },

    // KS 终端：SIM 卡网络运营商（10 未成功获取、11 联通、12 移动、13 电信、14 其他运营商）
    formatKsSimOperator(val) {
      if (val === null || val === undefined || val === "") return "-"
      const name = KS_SIM_OPERATORS[val]
      return name ? `${val}（${name}）` : String(val)
    },

    // KS 终端：SIM 卡网络信号等级（0~31，数值越大信号越好）
    formatKsSignal(val) {
      if (val === null || val === undefined || val === "") return "-"
      const signal = Number(val)
      if (isNaN(signal)) return "-"
      return `${signal} / 31`
    },

    // KS 终端：策略优先级（10 预测调控、15 动态调控、20 邀约调控、25 特殊调控，数值越大优先级越高）
    formatKsPriority(val) {
      if (val === null || val === undefined || val === "") return "-"
      const name = KS_PRIORITY_TYPES[val]
      return name ? `${val}（${name}）` : String(val)
    },

    // KS 终端：上报频率（秒）
    ksIntervalText(val) {
      if (val === null || val === undefined || val === "") return "-"
      return `${val} 秒`
    },

    // KS 终端：策略时间（unix 时间戳精确到秒 → MM-dd HH:mm）
    formatKsTime(val) {
      const seconds = Number(val)
      if (!seconds || isNaN(seconds)) return "-"
      const date = new Date(seconds * 1000)
      if (isNaN(date.getTime())) return "-"
      const pad = (num) => (num < 10 ? "0" + num : "" + num)
      return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    },

    // KS 终端：策略配置功率（精确到 0.1kW）
    formatKsPower(val) {
      const num = Number(val)
      if (val === null || val === undefined || val === "" || isNaN(num)) return "-"
      return `${num.toFixed(1)} kW`
    },

    // 打开 KS 终端设置弹窗（默认频率 300 秒）
    onOpenKsSetting() {
      const ksTerminal = this.ksTerminal
      // 无 KS 终端信息（接口未返回且未开启示例数据）时不可设置
      if (!ksTerminal) {
        this.$message.warning("无KS终端信息，不可设置")
        return
      }
      this.ksSettingForm = {
        report_interval:
          ksTerminal.report_interval != null ? Number(ksTerminal.report_interval) : KS_DEFAULT_REPORT_INTERVAL,
        switch_report_interval:
          ksTerminal.switch_report_interval != null
            ? Number(ksTerminal.switch_report_interval)
            : KS_DEFAULT_REPORT_INTERVAL
      }
      this.ksSettingVisible = true
    },

    // 保存 KS 终端设置
    onSaveKsSetting() {
      const form = this.ksSettingForm || {}
      if (!form.report_interval || !form.switch_report_interval) {
        this.$message.warning("请填写上报频率")
        return
      }
      const params = {
        transformer_id: this.nodeInfo && this.nodeInfo.ID,
        triplet_id: this.nodeInfo && this.nodeInfo.triplet_id,
        assetsCode: (this.nodeInfo && this.nodeInfo.asset_code) || "",
        report_interval: Number(form.report_interval),
        switch_report_interval: Number(form.switch_report_interval)
      }
      this.ksSettingSaving = true
      saveKsTerminalConfigApi(params).then(() => {
        // 保存成功后同步本地展示，避免重新拉取详情
        const ksTerminal = this.ksTerminal || {}
        this.nodeInfo = Object.assign({}, this.nodeInfo, {
          ks_terminal: Object.assign({}, ksTerminal, {
            report_interval: params.report_interval,
            switch_report_interval: params.switch_report_interval
          })
        })
        this.ksSettingVisible = false
        this.$message.success("设置已保存")
      }).catch(() => {
        this.$message.error("设置保存失败")
      }).finally(() => {
        this.ksSettingSaving = false
      })
    },

    // 设备上报值按比例还原（空值或非数字返回 null）
    toFixedNumber(value, divisor, digits) {
      const num = Number(value)
      if (value === null || value === undefined || value === "" || isNaN(num)) return null
      return Number((num / divisor).toFixed(digits))
    },

    // 电能量展示（kWh）
    formatEnergy(val) {
      if (val === null || val === undefined || val === "") return "-"
      const num = Number(val)
      if (isNaN(num)) return "-"
      return num.toFixed(2) + " kWh"
    },

    // 投运状态展示
    formatOperation(val) {
      if (val === null || val === undefined || val === "") return "-"
      return val === true || val === 1 || val === "true" ? "已投运" : "未投运"
    },

    // 点击第二层（国网电表箱）时：该节点及其下所有节点展开，同层其他节点收缩
    // 点击第三层（一体柜/有序充电控制箱）时：该节点展开，同层其他节点收缩
    focusTopoNode(node) {
      if (node.node_type === "meter_box") {
        this.hasFocus = true
        this.focusLevel2Uid = node.uid
        const firstLevel3Node = (node.children && node.children[0]) || null
        this.focusLevel3Uid = firstLevel3Node ? firstLevel3Node.uid : ""
      } else if (this.isLevel3Node(node)) {
        this.hasFocus = true
        this.focusLevel3Uid = node.uid
        const parent = this.findTopoParent(this.topoData, node.uid)
        this.focusLevel2Uid = parent ? parent.uid : ""
      }
    },

    // 按节点 uid 查找（uid 在 normalizeTopoNode 中生成，电表箱 id 为空时也可唯一定位）
    findTopoNode(node, uid) {
      if (!node || !uid) return null
      if (node.uid === uid) return node
      if (node.children && node.children.length) {
        for (let i = 0; i < node.children.length; i++) {
          const found = this.findTopoNode(node.children[i], uid)
          if (found) return found
        }
      }
      return null
    },

    findTopoNodeByName(node, name) {
      if (!node || !name) return null
      // 图表节点名称为格式化后的名称（分支箱含路数），需同时兼容原始名称
      if (node.name === name || this.formatNodeLabel(node) === name) return node
      if (node.children && node.children.length) {
        for (let i = 0; i < node.children.length; i++) {
          const found = this.findTopoNodeByName(node.children[i], name)
          if (found) return found
        }
      }
      return null
    },

    findTopoParent(node, uid) {
      if (!node || !node.children || !node.children.length) return null
      const matched = node.children.find((child) => child.uid === uid)
      if (matched) return node
      for (let i = 0; i < node.children.length; i++) {
        const found = this.findTopoParent(node.children[i], uid)
        if (found) return found
      }
      return null
    },

    /* ============ 分支箱通道 / 费控表 ============ */
    // 根据分支箱 ID 获取其下的通道与费控表数据（路数由接口返回决定）
    fetchFeeTableList(branchBox) {
      const branchBoxId = branchBox && branchBox.ID
      this.feeTableList = []
      if (!branchBoxId) return
      this.feeTableLoading = true
      getFeeTablelistApi({ power_branch_box_id: branchBoxId }).then((res) => {
        const output = this.pickApiPayload(res)
        this.feeTableList = this.normalizeFeeTableList(output.data || output.list || [])
      }).catch(() => {
        // 接口异常时不展示费控表数据
        this.feeTableList = []
      }).finally(() => {
        this.feeTableLoading = false
      })
    },

    // 统一费控表数据：按通道号排序、划分 A/B/C 相、计算安装/通信/绑定状态与公桩/私桩通道
    normalizeFeeTableList(list) {
      return (list || [])
        .map((item) => {
          const orderNum = Number(item.order_num) || 0
          const remainder = orderNum % 3
          // 通道号每 3 路循环：余1→A相、余2→B相、整除→C相
          const phase = remainder === 1 ? "A" : (remainder === 2 ? "B" : "C")
          const address = item.address ? String(item.address).trim() : ""
          // address 有值表示该通道安装了费控表，值为费控表电表地址；为空表示未安装
          const installed = !!address
          const balanceError = item.dev_balance_error === true
          const online = !!item.real_power_info
          // 绑定状态：is_band=1 或 user_bind 为真表示已绑用户（沿用既有费控表接口字段）
          const bound = item.is_band === 1 || item.is_band === "1" || item.is_band === true || !!item.user_bind
          // 公桩通道 / 私桩通道
          const isPublicChannel = this.resolveChannelPublic(item)
          return Object.assign({}, item, {
            order_num: orderNum,
            address,
            code: `M${orderNum < 10 ? "0" + orderNum : orderNum}`,
            phase,
            installed,
            bound,
            is_public_channel: isPublicChannel,
            channel_type_name: isPublicChannel ? "公桩通道" : "私桩通道",
            // 运行状态：offline=未安装、warning=余额异常、normal=正常
            run_status: !installed ? "offline" : (balanceError ? "warning" : "normal"),
            run_status_name: !installed ? "未安装" : (balanceError ? "余额异常" : "正常"),
            // 安装状态
            install_status_name: installed ? "已安装" : "未安装",
            // 通信状态：存在实时功率信息视为在线
            comm_status: installed && online ? "online" : "offline",
            comm_status_name: installed && online ? "在线" : "离线",
            // 绑定状态
            bind_status_name: bound ? "已绑用户" : "未绑用户"
          })
        })
        .sort((a, b) => a.order_num - b.order_num)
    },

    // 公/私桩通道：优先取接口返回的公桩标识（is_pub / is_public / pub）
    // 接口未返回该标识时默认按公桩通道展示
    resolveChannelPublic(item) {
      const source = item || {}
      const flag =
        source.is_pub !== undefined ? source.is_pub : (source.is_public !== undefined ? source.is_public : source.pub)
      if (flag !== null && flag !== undefined && flag !== "") {
        return flag === true || flag === 1 || flag === "1" || flag === "true"
      }
      return true
    },

    getPhaseMeters(phase) {
      return this.feeTableList.filter((item) => item.phase === phase)
    },

    /* ============ 三相监测数据（第三层一体柜/控制箱、第四层分支箱共用） ============ */
    // 是否需要展示三相监测数据（第三层一体柜/控制箱、第四层分支箱）
    isPhaseMonitorNode(node) {
      return !!node && (this.isLevel3Type(node.node_type) || node.node_type === "branch_box")
    },

    // 有资产码且当前无数据、未在请求中时，发起三相监测数据请求
    // 说明：拓扑树节点未返回资产码，需等节点详情接口返回 triplet.assetsCode 后再调用
    fetchPhaseMonitorIfNeeded(node) {
      if (!this.isPhaseMonitorNode(node)) return
      if (this.phaseMonitorData || this.phaseMonitorLoading) return
      const assetsCode = this.resolveAssetsCode(node)
      if (!assetsCode) return
      this.fetchPhaseMonitor(node, assetsCode)
    },

    // 节点资产码：优先节点自身（拓扑树已带 asset_code），其次取详情接口合并后的当前节点资产码
    resolveAssetsCode(node) {
      if (!node) return ""
      const ownCode = node.asset_code || node.assets_code || ""
      if (ownCode) return ownCode
      if (this.nodeInfo && this.nodeInfo.uid === node.uid) {
        return this.nodeInfo.asset_code || ""
      }
      return ""
    },

    // 按节点资产码获取近 24 小时 AC 历史数据（一体柜与分支箱合并为同一接口）
    fetchPhaseMonitor(node, code) {
      const assetsCode = code || this.resolveAssetsCode(node)
      this.phaseMonitorData = null
      // 请求序号：快速切换节点时丢弃过期响应
      this.phaseMonitorSeq += 1
      if (!assetsCode) return
      const seq = this.phaseMonitorSeq
      const params = this.buildPhaseMonitorParams(assetsCode)
      this.phaseMonitorLoading = true
      getAcHisListApi(params).then((res) => {
        if (seq !== this.phaseMonitorSeq) return
        const payload = this.pickApiPayload(res)
        const list = (payload && (payload.data || payload.list)) || []
        this.phaseMonitorData = list.length ? this.buildPhaseMonitorData(list) : null
      }).catch(() => {
        // 接口异常时不展示三相监测数据
        if (seq !== this.phaseMonitorSeq) return
        this.phaseMonitorData = null
      }).finally(() => {
        if (seq !== this.phaseMonitorSeq) return
        this.phaseMonitorLoading = false
        this.$nextTick(() => {
          this.renderPhaseCharts()
        })
      })
    },

    // 三相监测查询参数：资产码 + 近 24 小时时间段
    buildPhaseMonitorParams(assetsCode) {
      const end = new Date()
      const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
      return {
        assetsCode,
        start_time: this.formatDateTime(start),
        end_time: this.formatDateTime(end)
      }
    },

    // 接口数据转换为曲线数据：电压 /10、电流 /100、功率 /100、温度 /100（与设备历史数据口径一致）
    buildPhaseMonitorData(list) {
      const rows = this.sampleAcHisData(list)
      const data = {
        times: [],
        voltage_a: [],
        voltage_b: [],
        voltage_c: [],
        current_a: [],
        current_b: [],
        current_c: [],
        power_a: [],
        power_b: [],
        power_c: [],
        power_total: [],
        temp_a: [],
        temp_b: [],
        temp_c: [],
        temp_n: [],
        temp_device: [],
        has_phase_temp: false,
        has_device_temp: false,
        has_total_power: false
      }
      rows.forEach((item) => {
        const voltageA = this.toFixedNumber(item.acVolA, 10, 1)
        const voltageB = this.toFixedNumber(item.acVolB, 10, 1)
        const voltageC = this.toFixedNumber(item.acVolC, 10, 1)
        const currentA = this.toFixedNumber(item.acCurA, 100, 2)
        const currentB = this.toFixedNumber(item.acCurB, 100, 2)
        const currentC = this.toFixedNumber(item.acCurC, 100, 2)
        data.times.push(this.formatTimeLabel(item.create_time))
        data.voltage_a.push(voltageA)
        data.voltage_b.push(voltageB)
        data.voltage_c.push(voltageC)
        data.current_a.push(currentA)
        data.current_b.push(currentB)
        data.current_c.push(currentC)
        // 分相功率优先取接口值，缺失时按 电压×电流 估算（kW）
        data.power_a.push(this.resolvePhasePower(item.realAPower, voltageA, currentA))
        data.power_b.push(this.resolvePhasePower(item.realBPower, voltageB, currentB))
        data.power_c.push(this.resolvePhasePower(item.realCPower, voltageC, currentC))
        const totalPower = this.toFixedNumber(item.realPower, 100, 3)
        if (totalPower != null) data.has_total_power = true
        data.power_total.push(totalPower)
        // 温度：接口返回分相温度时分相展示，同时展示设备温度（枪温/柜温）
        const tempA = this.toFixedNumber(item.tempA, 100, 1)
        const tempB = this.toFixedNumber(item.tempB, 100, 1)
        const tempC = this.toFixedNumber(item.tempC, 100, 1)
        const tempN = this.toFixedNumber(item.tempN, 100, 1)
        const deviceTemp = this.toFixedNumber(item.gunTemp, 100, 1)
        if (tempA != null || tempB != null || tempC != null) data.has_phase_temp = true
        if (deviceTemp != null) data.has_device_temp = true
        data.temp_a.push(tempA)
        data.temp_b.push(tempB)
        data.temp_c.push(tempC)
        data.temp_n.push(tempN)
        data.temp_device.push(deviceTemp)
      })
      return data
    },

    // 分相功率：优先取接口值（/100），缺失时按 电压×电流 估算（kW）
    resolvePhasePower(rawPower, voltage, current) {
      const power = this.toFixedNumber(rawPower, 100, 3)
      if (power != null) return power
      if (voltage == null || current == null) return null
      return Number(((voltage * current) / 1000).toFixed(3))
    },

    // 数据点过多时等间隔抽样（最多 288 个点），保证曲线可读
    sampleAcHisData(list) {
      const rows = Array.isArray(list) ? list : []
      const maxPoints = 288
      if (rows.length <= maxPoints) return rows
      const step = Math.ceil(rows.length / maxPoints)
      const sampled = []
      for (let i = 0; i < rows.length; i += step) sampled.push(rows[i])
      if (sampled[sampled.length - 1] !== rows[rows.length - 1]) sampled.push(rows[rows.length - 1])
      return sampled
    },

    // 曲线 X 轴时间标签（yyyy-MM-dd HH:mm:ss → HH:mm）
    formatTimeLabel(value) {
      if (!value) return ""
      const text = String(value)
      const match = text.match(/(\d{2}):(\d{2})/)
      return match ? `${match[1]}:${match[2]}` : text
    },

    // 日期格式化：yyyy-MM-dd HH:mm:ss（接口时间参数格式）
    formatDateTime(date) {
      const time = date instanceof Date ? date : new Date(date)
      if (!time || isNaN(time.getTime())) return ""
      const pad = (val) => (val < 10 ? "0" + val : "" + val)
      return `${time.getFullYear()}-${pad(time.getMonth() + 1)}-${pad(time.getDate())} ${pad(time.getHours())}:${pad(time.getMinutes())}:${pad(time.getSeconds())}`
    },

    // 四张曲线的系列配置：相色 A=红、B=橙、C=蓝；功率含总功率，温度按接口实际返回的数据动态展示
    getPhaseCurveConfigs() {
      const series = (names, dataKeys, colors) => names.map((name, index) => ({
        name,
        dataKey: dataKeys[index],
        color: colors[index]
      }))
      const phaseColors = ["#F56C6C", "#E6A23C", "#409EFF"]
      const data = this.phaseMonitorData || {}

      const powerSeries = series(["A相功率", "B相功率", "C相功率"], ["power_a", "power_b", "power_c"], phaseColors)
      if (data.has_total_power) {
        powerSeries.push({ name: "总功率", dataKey: "power_total", color: "#9C27B0" })
      }

      const tempSeries = []
      if (data.has_phase_temp) {
        series(
          ["A相温度", "B相温度", "C相温度", "N相温度"],
          ["temp_a", "temp_b", "temp_c", "temp_n"],
          ["#F56C6C", "#E6A23C", "#409EFF", "#909399"]
        ).forEach((item) => tempSeries.push(item))
      }
      if (data.has_device_temp) {
        tempSeries.push({ name: "设备温度", dataKey: "temp_device", color: "#9C27B0" })
      }

      return [
        {
          refName: "phaseVoltageChart",
          unit: "V",
          series: series(["A相电压", "B相电压", "C相电压"], ["voltage_a", "voltage_b", "voltage_c"], phaseColors)
        },
        {
          refName: "phaseCurrentChart",
          unit: "A",
          series: series(["A相电流", "B相电流", "C相电流"], ["current_a", "current_b", "current_c"], phaseColors)
        },
        {
          refName: "phasePowerChart",
          unit: "kW",
          series: powerSeries
        },
        {
          refName: "phaseTempChart",
          unit: "℃",
          series: tempSeries
        }
      ]
    },

    // 渲染四张三相曲线（电压 / 电流 / 功率 / 温度）
    renderPhaseCharts() {
      if (!this.phaseMonitorData) return
      this.getPhaseCurveConfigs().forEach((config) => {
        this.renderPhaseCurve(config)
      })
    },

    renderPhaseCurve(config) {
      const el = this.$refs[config.refName]
      if (!el) return
      if (!this.phaseCharts) this.phaseCharts = {}
      let chart = this.phaseCharts[config.refName]
      // 容器被重建时销毁旧实例，避免图表渲染到已移除的 DOM 上
      if (chart && chart.getDom() !== el) {
        chart.dispose()
        chart = null
      }
      if (!chart) {
        chart = echarts.init(el)
        this.phaseCharts[config.refName] = chart
      }
      chart.clear()
      chart.setOption(this.buildPhaseCurveOption(config), true)
      chart.resize()
    },

    buildPhaseCurveOption(config) {
      const data = this.phaseMonitorData || {}
      return {
        tooltip: { trigger: "axis" },
        legend: {
          data: config.series.map((item) => item.name),
          top: 0,
          itemWidth: 12,
          itemHeight: 8,
          textStyle: { fontSize: 11 }
        },
        grid: { left: 46, right: 16, top: 34, bottom: 26 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: data.times || [],
          axisLabel: { fontSize: 10, color: "#909399" },
          axisLine: { lineStyle: { color: "#dcdfe6" } }
        },
        yAxis: {
          type: "value",
          name: config.unit,
          scale: true,
          nameTextStyle: { fontSize: 11, color: "#909399" },
          axisLabel: { fontSize: 10, color: "#909399" },
          splitLine: { lineStyle: { color: "#f2f6fc" } }
        },
        series: config.series.map((item) => ({
          name: item.name,
          type: "line",
          smooth: true,
          symbol: "none",
          data: data[item.dataKey] || [],
          itemStyle: { color: item.color },
          lineStyle: { color: item.color },
          areaStyle: { color: item.color, opacity: 0.06 }
        }))
      }
    },

    // 销毁三相曲线图表
    disposePhaseCharts() {
      if (!this.phaseCharts) return
      Object.keys(this.phaseCharts).forEach((refName) => {
        const chart = this.phaseCharts[refName]
        if (chart) chart.dispose()
      })
      this.phaseCharts = {}
    },

    // 点击某一路费控表：右侧展示该表监测数据（未安装费控表的通道不可查看）
    onViewMeterMonitor(meter) {
      if (!meter || !meter.installed) {
        this.$message.warning(`通道 ${(meter && meter.code) || ""} 未安装费控表`)
        return
      }
      this.currentMeter = { ...meter }
      // 节点信息视图被切换后其 DOM 会被移除，先销毁三相曲线图表实例
      this.disposePhaseCharts()
      // 图表容器随视图重建，先销毁旧实例
      this.disposeMeterChart()
      this.infoView = "meter"
      this.meterLoading = true
      this.meterMonitorData = null
      const params = this.buildMeterMonitorParams(meter)
      if (!params.assetsCode) {
        // 缺少资产码时无法按设备查询历史数据
        this.meterLoading = false
        return
      }
      getFeeMeterMonitorDataApi(params).then((res) => {
        const payload = this.pickApiPayload(res)
        const list = (payload && (payload.data || payload.list)) || []
        this.meterMonitorData = list.length ? this.buildMeterMonitorData(list) : null
      }).catch(() => {
        // 接口异常时不展示监测数据
        this.meterMonitorData = null
      }).finally(() => {
        this.meterLoading = false
        this.$nextTick(() => {
          this.renderMeterChart()
        })
      })
    },

    // 费控表监测查询参数：所属分支箱资产码 + 通道号 + 近 24 小时时间段
    buildMeterMonitorParams(meter) {
      console.log('构建费控表监测查询请求参数的对象',meter)
      const end = new Date()
      const start = new Date(end.getTime() - 24 * 60 * 60 * 1000)
      const assetsCode = (meter && (meter.assets_code || meter.assetsCode)) || (this.nodeInfo && this.nodeInfo.asset_code) || ""
      return {
        // 费控表挂在分支箱设备下：按分支箱资产码查询设备历史数据，再用通道号过滤
        assetsCode,
        gun_no: meter.order_num || 0 ,
        start_time: this.formatDateTime(start),
        end_time: this.formatDateTime(end)
      }
    },

    // 费控表历史数据转换为图表数据：电压 /10、电流 /100、功率 /100（与设备上报口径一致）
    buildMeterMonitorData(list) {
      const rows = this.sampleAcHisData(list)
      const data = { times: [], voltage_list: [], current_list: [], power_list: [] }
      rows.forEach((item) => {
        data.times.push(this.formatTimeLabel(item.create_time))
        data.voltage_list.push(this.pickAcValue(item, ["acVolA", "acVolB", "acVolC"], 10, 1))
        data.current_list.push(this.pickAcValue(item, ["acCurA", "acCurB", "acCurC"], 100, 2))
        data.power_list.push(this.toFixedNumber(item.realPower, 100, 3))
      })
      return data
    },

    // 依次取第一个有值的相数据（费控表可能只上报其中一相）
    pickAcValue(item, keys, divisor, digits) {
      for (let i = 0; i < keys.length; i++) {
        const value = this.toFixedNumber(item[keys[i]], divisor, digits)
        if (value != null) return value
      }
      return null
    },

    // 返回费控表所属分支箱的节点信息
    onBackToNodeInfo() {
      // 先销毁图表实例（dispose 会一并移除容器内 ECharts 生成的 DOM，避免返回后残留空白）
      this.disposeMeterChart()
      this.infoView = "node"
      this.currentMeter = {}
      this.meterMonitorData = null
      // 节点信息视图重建后重新渲染三相曲线（分支箱 / 一体柜）
      this.$nextTick(() => {
        this.renderPhaseCharts()
      })
    },

    // 销毁费控表监测图表
    disposeMeterChart() {
      if (this.meterChart) {
        this.meterChart.dispose()
        this.meterChart = null
      }
    },

    renderMeterChart() {
      const data = this.meterMonitorData
      if (!data || !this.$refs.meterChart) return
      // 容器被重建时销毁旧实例，避免图表渲染到已移除的 DOM 上
      if (this.meterChart && this.meterChart.getDom() !== this.$refs.meterChart) {
        this.disposeMeterChart()
      }
      if (!this.meterChart) {
        this.meterChart = echarts.init(this.$refs.meterChart)
      }
      this.meterChart.clear()
      this.meterChart.setOption(this.buildMeterOption(data), true)
      this.meterChart.resize()
    },

    buildMeterOption(data) {
      return {
        tooltip: { trigger: "axis" },
        legend: { data: ["电压(V)", "电流(A)", "功率(kW)"], top: 0, itemWidth: 14, itemHeight: 8, textStyle: { fontSize: 12 } },
        grid: { left: 52, right: 78, top: 40, bottom: 32 },
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: data.times || [],
          axisLabel: { fontSize: 11, color: "#909399" },
          axisLine: { lineStyle: { color: "#dcdfe6" } }
        },
        yAxis: [
          {
            type: "value",
            name: "电压(V)",
            min: 200,
            max: 240,
            nameTextStyle: { fontSize: 11, color: "#409EFF" },
            axisLabel: { fontSize: 11, color: "#909399" },
            splitLine: { lineStyle: { color: "#f2f6fc" } }
          },
          {
            type: "value",
            name: "电流(A)",
            position: "right",
            nameTextStyle: { fontSize: 11, color: "#E6A23C" },
            axisLabel: { fontSize: 11, color: "#909399" },
            splitLine: { show: false }
          },
          {
            type: "value",
            name: "功率(kW)",
            position: "right",
            offset: 46,
            nameTextStyle: { fontSize: 11, color: "#67C23A" },
            axisLabel: { fontSize: 11, color: "#909399" },
            splitLine: { show: false }
          }
        ],
        series: [
          {
            name: "电压(V)",
            type: "line",
            smooth: true,
            symbol: "none",
            data: data.voltage_list || [],
            itemStyle: { color: "#409EFF" },
            areaStyle: { color: "rgba(64, 158, 255, 0.08)" }
          },
          {
            name: "电流(A)",
            type: "line",
            smooth: true,
            symbol: "none",
            yAxisIndex: 1,
            data: data.current_list || [],
            itemStyle: { color: "#E6A23C" },
            areaStyle: { color: "rgba(230, 162, 60, 0.08)" }
          },
          {
            name: "功率(kW)",
            type: "line",
            smooth: true,
            symbol: "none",
            yAxisIndex: 2,
            data: data.power_list || [],
            itemStyle: { color: "#67C23A" },
            areaStyle: { color: "rgba(103, 194, 58, 0.08)" }
          }
        ]
      }
    },

    onCloseTopo() {
      this.topoVisible = false
      this.currentTransformer = {}
      this.topoData = null
      this.nodeInfo = {}
      this.nodeFeeStat = Object.assign({}, NODE_FEE_STAT_DEMO.transformer)
      this.nodeDetailLoading = false
      this.nodeDetailSeq = 0
      this.ksSettingVisible = false
      this.selectedNodeUid = ""
      this.infoView = "node"
      this.currentMeter = {}
      this.meterMonitorData = null
      this.feeTableList = []
      this.phaseMonitorData = null
      this.phaseMonitorSeq = 0
      if (this.topoChart) {
        this.topoChart.dispose()
        this.topoChart = null
      }
      this.disposeMeterChart()
      this.disposePhaseCharts()
    },

    onWindowResize() {
      if (this.topoChart) {
        // 窗口尺寸变化时重算画布宽度（保证宽度自适应容器、节点完整展示）
        this.topoChartWidth = this.calcTopoChartWidth()
        this.topoChart.resize({ width: this.topoChartWidth, height: this.topoChartHeight })
      }
      if (this.meterChart) {
        this.meterChart.resize()
      }
      if (this.phaseCharts) {
        Object.keys(this.phaseCharts).forEach((refName) => {
          const chart = this.phaseCharts[refName]
          if (chart) chart.resize()
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.transformer-overview {
  height: calc(100vh - 120px);
  box-sizing: border-box;
  overflow-y: auto;

  .search-card,
  .statistic-card,
  .table-card,
  .topo-card {
    flex-shrink: 0;
    margin-bottom: 16px;
  }

  .search-card {
    ::v-deep .el-card__body {
      padding: 14px 16px 4px;
    }

    ::v-deep .el-form-item {
      margin-right: 12px;
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .card-title {
      font-size: 15px;
      font-weight: 600;
      color: #303133;
    }

    .card-sub-title {
      font-size: 13px;
      color: #909399;
    }
  }

  .table-card {
    ::v-deep .el-card__body {
      padding: 16px;
    }
  }

  /* ========== 数据统计 ========== */
  .statistic-card {
    ::v-deep .el-card__body {
      padding: 16px;
    }
  }

  .stat-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;

    .stat-item {
      position: relative;
      padding: 14px 16px;
      min-height: 80px;
      border: 1px solid #ebeef5;
      border-radius: 6px;
      background-color: #fafcff;
      box-sizing: border-box;
      overflow: hidden;
      transition: transform 0.2s, box-shadow 0.2s;

      &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        background-color: var(--stat-color, #409eff);
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
      }

      .stat-item-label {
        display: flex;
        align-items: center;
        font-size: 13px;
        color: #606266;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        .stat-item-dot {
          flex-shrink: 0;
          width: 6px;
          height: 6px;
          margin-right: 6px;
          border-radius: 50%;
          background-color: var(--stat-color, #409eff);
        }
      }

      .stat-item-value {
        margin-top: 8px;
        font-size: 24px;
        font-weight: 700;
        line-height: 1.2;
        color: #303133;
      }

      .stat-item-unit {
        margin-left: 4px;
        font-size: 12px;
        font-weight: 400;
        color: #909399;
      }
    }

    /* 台区总数为重点指标，占据两列并使用主色渐变 */
    .stat-item-primary {
      grid-column: span 2;
      border: none;
      color: #ffffff;
      background: linear-gradient(135deg, #66b1ff 0%, #409eff 100%);
      box-shadow: 0 2px 10px rgba(64, 158, 255, 0.24);

      &::before {
        display: none;
      }

      .stat-item-label {
        color: rgba(255, 255, 255, 0.95);

        .stat-item-dot {
          background-color: #ffffff;
        }
      }

      .stat-item-value {
        font-size: 30px;
        color: #ffffff;
      }

      .stat-item-unit {
        color: rgba(255, 255, 255, 0.85);
      }
    }

    .stat-blue {
      --stat-color: #409eff;
    }

    .stat-cyan {
      --stat-color: #13c2c2;
    }

    .stat-green {
      --stat-color: #67c23a;
    }

    .stat-purple {
      --stat-color: #9254de;
    }

    .stat-orange {
      --stat-color: #e6a23c;
    }

    .stat-indigo {
      --stat-color: #597ef7;
    }

    .stat-red {
      --stat-color: #f56c6c;
    }
  }

  .stat-demo-tip {
    display: flex;
    align-items: center;
    margin-top: 10px;
    font-size: 12px;
    color: #e6a23c;

    i {
      margin-right: 4px;
    }
  }

  @media (max-width: 1200px) {
    .stat-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  /* ========== 拓扑图展示区 ========== */
  .topo-card {
    ::v-deep .el-card__body {
      padding: 16px;
    }
  }

  .topo-layout {
    display: flex;
    align-items: flex-start;
  }

  /* 左侧拓扑图占 60%，高度不限制 */
  .topo-chart-wrap {
    width: 60%;
    flex: 0 0 60%;
    min-width: 0;
    padding-right: 10px;
    box-sizing: border-box;
  }

  /* 宽度不足时出现横向滚动条，高度不限制（全部节点完整展示） */
  .topo-chart-scroll {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #fafcff;

    &::-webkit-scrollbar {
      height: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #c0c4cc;
      border-radius: 5px;
    }
  }

  .topo-chart {
    display: block;
  }

  .topo-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    padding-top: 10px;

    .topo-legend-item {
      display: flex;
      align-items: center;
      margin-right: 20px;
      font-size: 13px;
      color: #606266;
    }

    .topo-legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-right: 6px;
    }

    .topo-legend-tip {
      font-size: 12px;
      color: #909399;
    }
  }

  /* 右侧信息区：宽度 40%，高度随内容自适应（不限制），宽度不足时横向滚动查看 */
  .topo-info-wrap {
    width: 40%;
    flex: 0 0 40%;
    min-width: 0;
    padding-left: 10px;
    box-sizing: border-box;
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #c0c4cc;
      border-radius: 5px;
    }
  }

  .topo-info-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;

    .topo-info-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
    }
  }

  .terminal-info {
    .terminal-line {
      line-height: 22px;
    }
  }

  .meter-body {
    min-width: 460px;
    box-sizing: border-box;
  }

  .meter-chart {
    width: 100%;
    height: 380px;
  }

  .meter-empty {
    padding: 60px 0;
    text-align: center;
    font-size: 13px;
    color: #909399;
  }

  /* ========== 节点费控统计（台区 / 一体柜 / 分支箱） ========== */
  .node-fee-panel {
    min-width: 420px;
    margin-top: 16px;
    padding: 14px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #ffffff;
    box-sizing: border-box;

    .node-fee-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;
    }

    .node-fee-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 10px;
    }

    .node-fee-item {
      padding: 10px 12px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      background-color: #fafcff;
      box-sizing: border-box;
    }

    .node-fee-label {
      font-size: 12px;
      color: #909399;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .node-fee-value {
      margin-top: 6px;
      font-size: 20px;
      font-weight: 700;
      line-height: 1.2;
      color: #303133;
    }

    .node-fee-unit {
      margin-left: 4px;
      font-size: 12px;
      font-weight: 400;
      color: #909399;
    }
  }

  /* ========== 台区 KS 终端信息 ========== */
  .ks-panel {
    margin-top: 16px;
    padding: 14px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #ffffff;
    box-sizing: border-box;

    .ks-panel-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 10px;
    }

    .ks-panel-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;

      .ks-placeholder-tag {
        margin-left: 6px;
        font-weight: 400;
        transform: scale(0.9);
      }
    }

    .ks-section-title {
      font-size: 12px;
      font-weight: 600;
      color: #606266;
      margin: 12px 0 8px;

      &:first-of-type {
        margin-top: 0;
      }
    }

    .ks-value-list {
      display: flex;
      flex-wrap: wrap;
    }

    .ks-value-tag {
      margin: 0 4px 4px 0;
    }

    .ks-empty {
      font-size: 13px;
      color: #909399;
      padding: 8px 0;
    }
  }

  /* ========== 三相监测曲线（一体柜 / 分支箱） ========== */
  .phase-curve-panel {
    min-width: 460px;
    margin-top: 16px;
    padding: 14px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #ffffff;
    box-sizing: border-box;

    .phase-curve-title {
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;
    }

    .phase-curve-sub-title {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }

    .phase-curve-empty {
      font-size: 13px;
      color: #909399;
      padding: 8px 0;
    }

    .phase-curve-item {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .phase-curve-item-title {
      font-size: 12px;
      color: #606266;
      margin-bottom: 6px;
    }

    .phase-curve-chart {
      width: 100%;
      min-width: 430px;
      height: 220px;
    }
  }

  /* ========== 分支箱通道 / 费控表状态 ========== */
  .fee-meter-panel {
    width: max-content;
    min-width: 100%;
    margin-top: 16px;
    padding: 14px;
    border: 1px solid #ebeef5;
    border-radius: 4px;
    background-color: #ffffff;
    box-sizing: border-box;

    .fee-meter-title {
      display: flex;
      align-items: center;
      font-size: 14px;
      font-weight: 600;
      color: #303133;
      margin-bottom: 10px;

      .fee-meter-help {
        margin-left: 6px;
        font-size: 14px;
        font-weight: 400;
        color: #909399;
        cursor: pointer;

        &:hover {
          color: #409eff;
        }
      }
    }

    .fee-meter-sub-title {
      font-size: 12px;
      font-weight: 400;
      color: #909399;
      margin-left: 8px;
    }

    .fee-meter-empty {
      font-size: 13px;
      color: #909399;
      padding: 8px 0;
    }

    .fee-meter-phase {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .fee-meter-phase-label {
      font-size: 12px;
      margin-bottom: 8px;
      color: #606266;

      &.fee-meter-phase-A {
        color: #e11d48;
      }

      &.fee-meter-phase-B {
        color: #d97706;
      }

      &.fee-meter-phase-C {
        color: #059669;
      }
    }

    .fee-meter-list {
      display: flex;
      flex-wrap: nowrap;
    }

    .fee-meter-item {
      width: 112px;
      flex-shrink: 0;
      padding: 8px 10px;
      margin: 0 8px 8px 0;
      border: 1px solid #dcdfe6;
      border-radius: 4px;
      background-color: #ffffff;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
      }

      &.fee-meter-item-normal {
        border-color: #67c23a;
      }

      &.fee-meter-item-warning {
        border-color: #e6a23c;
      }

      &.fee-meter-item-offline {
        border-color: #dcdfe6;
      }

      /* 未安装费控表的通道 */
      &.fee-meter-item-disabled {
        background-color: #f8fafc;
        border-style: dashed;
        cursor: not-allowed;
        opacity: 0.7;

        &:hover {
          transform: none;
          box-shadow: none;
        }
      }

      .fee-meter-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .fee-meter-code {
        font-size: 12px;
        font-weight: 600;
        color: #303133;
      }

      /* 公桩通道 / 私桩通道标识 */
      .fee-meter-channel {
        width: 16px;
        height: 16px;
        line-height: 16px;
        text-align: center;
        border-radius: 3px;
        font-size: 11px;
        font-style: normal;
        color: #ffffff;

        &.fee-meter-channel-public {
          background-color: #409eff;
        }

        &.fee-meter-channel-private {
          background-color: #e6a23c;
        }
      }

      /* 三个状态：方形=安装状态、圆形=通信状态、三角形=绑定状态 */
      .fee-meter-states {
        display: flex;
        align-items: center;
        margin-top: 8px;
      }

      .fee-meter-state {
        display: inline-block;
        margin-right: 9px;

        &:last-child {
          margin-right: 0;
        }

        &.state-install {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          background-color: #dcdfe6;

          &.state-on {
            background-color: #67c23a;
          }
        }

        &.state-comm {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background-color: #dcdfe6;

          &.state-on {
            background-color: #409eff;
          }
        }

        &.state-bind {
          width: 0;
          height: 0;
          background-color: transparent;
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 9px solid #dcdfe6;

          &.state-on {
            border-bottom-color: #e6a23c;
          }
        }
      }
    }
  }
}

/* 费控表状态说明气泡（内容挂载在 body 下，不能嵌套在页面根节点内） */
.fee-meter-tips {
  font-size: 12px;
  line-height: 20px;

  .fee-meter-tips-row {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    white-space: nowrap;

    & + .fee-meter-tips-row {
      margin-top: 4px;
    }
  }

  .fee-meter-tips-label {
    color: #dcdfe6;
    margin-right: 4px;
  }

  .fee-meter-tips-item {
    display: inline-flex;
    align-items: center;
    margin-right: 10px;

    &:last-child {
      margin-right: 0;
    }
  }

  /* 三个状态图形：方形=安装、圆形=通信、三角形=绑定 */
  .fee-meter-tips-shape {
    display: inline-block;
    margin-right: 4px;

    &.shape-install {
      width: 10px;
      height: 10px;
      border-radius: 2px;
      background-color: #dcdfe6;

      &.shape-on {
        background-color: #67c23a;
      }
    }

    &.shape-comm {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: #dcdfe6;

      &.shape-on {
        background-color: #409eff;
      }
    }

    &.shape-bind {
      width: 0;
      height: 0;
      background-color: transparent;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 9px solid #dcdfe6;

      &.shape-on {
        border-bottom-color: #e6a23c;
      }
    }
  }

  /* 公桩 / 私桩通道标识 */
  .fee-meter-tips-channel {
    display: inline-block;
    width: 16px;
    height: 16px;
    line-height: 16px;
    margin-right: 4px;
    border-radius: 3px;
    text-align: center;
    font-size: 11px;
    font-style: normal;
    color: #ffffff;

    &.channel-public {
      background-color: #409eff;
    }

    &.channel-private {
      background-color: #e6a23c;
    }
  }
}

/* KS 终端设置弹窗（对话框挂载在 body 下，不能嵌套在页面根节点内） */
.ks-form-unit {
  margin-left: 8px;
  font-size: 12px;
  color: #606266;
}
</style>
